import { AdminLayout } from "./AdminLayout";
import { Plus, Search, MoreVertical, GripVertical, Eye, EyeOff, Trash2, Edit2, ExternalLink } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '', slug: '', tagline: '', description: '', icon: 'globe',
    pricing_from: '', pricing_currency: 'GBP', featured_image: '', order: 0, is_featured: false, status: 'draft'
  });

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/cms/services`, { withCredentials: true });
      setServices(response.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      // Services endpoint may not exist yet - use empty array
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        slug: formData.slug || generateSlug(formData.title),
        pricing_from: formData.pricing_from ? parseFloat(formData.pricing_from) : null,
        order: parseInt(formData.order) || 0
      };
      
      if (editingItem) {
        await axios.put(`${API}/cms/services/${editingItem.service_id}`, data, { withCredentials: true });
        toast.success("Service updated");
      } else {
        await axios.post(`${API}/cms/services`, data, { withCredentials: true });
        toast.success("Service created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '', slug: item.slug || '', tagline: item.tagline || '',
      description: item.description || '', icon: item.icon || 'globe',
      pricing_from: item.pricing_from?.toString() || '', pricing_currency: item.pricing_currency || 'GBP',
      featured_image: item.featured_image || '', order: item.order || 0,
      is_featured: item.is_featured || false, status: item.status || 'draft'
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await axios.delete(`${API}/cms/services/${id}`, { withCredentials: true });
      toast.success("Service deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/cms/services/${item.service_id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Service ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ title: '', slug: '', tagline: '', description: '', icon: 'globe',
      pricing_from: '', pricing_currency: 'GBP', featured_image: '', order: 0, is_featured: false, status: 'draft' });
  };

  const statusColors = {
    draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    published: 'bg-green-500/20 text-green-400 border-green-500/30',
    archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };

  const iconOptions = [
    { value: 'globe', label: 'Globe (Websites)' },
    { value: 'layout', label: 'Layout (Web Apps)' },
    { value: 'smartphone', label: 'Smartphone (Mobile)' },
    { value: 'link', label: 'Link (Integrations)' },
    { value: 'headphones', label: 'Headphones (Support)' },
    { value: 'zap', label: 'Zap (Fast)' },
    { value: 'code', label: 'Code (Development)' },
    { value: 'database', label: 'Database (Data)' }
  ];

  return (
    <AdminLayout title="Services">
      <div className="space-y-6" data-testid="admin-services">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Services</h2>
            <p className="text-sm text-[#8A8A8A]">Service offerings for public website</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Service
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingItem ? 'Edit' : 'New'} Service</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <input type="text" placeholder="Service Title *" required value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})} className="input-field" />
                <input type="text" placeholder="URL Slug" value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})} className="input-field" />
                <input type="text" placeholder="Tagline" value={formData.tagline}
                  onChange={(e) => setFormData({...formData, tagline: e.target.value})} className="input-field" />
                <textarea placeholder="Description" rows={4} value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})} className="input-field resize-none" />
                <div className="grid grid-cols-2 gap-4">
                  <select value={formData.icon} onChange={(e) => setFormData({...formData, icon: e.target.value})} className="input-field">
                    {iconOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                  <input type="number" placeholder="Display Order" value={formData.order}
                    onChange={(e) => setFormData({...formData, order: e.target.value})} className="input-field" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input type="number" placeholder="Price From (£)" value={formData.pricing_from}
                    onChange={(e) => setFormData({...formData, pricing_from: e.target.value})} className="input-field" />
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="input-field">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <input type="url" placeholder="Featured Image URL" value={formData.featured_image}
                  onChange={(e) => setFormData({...formData, featured_image: e.target.value})} className="input-field" />
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})} className="w-4 h-4" />
                  <span className="text-sm">Featured service (shown on homepage)</span>
                </label>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">{editingItem ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-4 text-sm">
          <p className="text-[#6B7280]">
            <span className="text-[#F5F4EF] font-medium">Note:</span> Services are currently managed via static pages. 
            Use this CMS for dynamic content.
          </p>
        </div>

        <div className="space-y-2">
          {loading ? (
            [...Array(5)].map((_, i) => (
              <div key={i} className="bg-[#1C1C1C] border border-[#2E2E2E] h-16 animate-pulse" />
            ))
          ) : services.length === 0 ? (
            <div className="text-center py-12 text-[#8A8A8A] border border-[#2E2E2E]">
              No services configured yet. Static service pages are being used.
            </div>
          ) : (
            services.map((item, index) => (
              <div key={item.service_id} className="bg-[#1C1C1C] border border-[#2E2E2E] p-4 flex items-center gap-4">
                <div className="text-[#8A8A8A] cursor-move">
                  <GripVertical className="w-5 h-5" />
                </div>
                <div className="w-10 h-10 bg-[#5F8FB8]/10 flex items-center justify-center text-[#5F8FB8] text-sm font-bold">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold truncate">{item.title}</h3>
                    {item.is_featured && <span className="text-xs text-[#5F8FB8]">Featured</span>}
                  </div>
                  <p className="text-sm text-[#8A8A8A] truncate">{item.tagline || item.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {item.pricing_from && (
                    <span className="text-sm text-[#8A8A8A]">From £{item.pricing_from.toLocaleString()}</span>
                  )}
                  <span className={`px-2 py-0.5 text-xs border ${statusColors[item.status]}`}>{item.status}</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 hover:bg-[#2E2E2E]"><MoreVertical className="w-4 h-4" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                      <DropdownMenuItem onClick={() => handleEdit(item)} className="cursor-pointer hover:bg-[#2E2E2E]">
                        <Edit2 className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleStatus(item)} className="cursor-pointer hover:bg-[#2E2E2E]">
                        {item.status === 'published' ? <><EyeOff className="w-4 h-4 mr-2" /> Unpublish</> : <><Eye className="w-4 h-4 mr-2" /> Publish</>}
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer hover:bg-[#2E2E2E]">
                        <a href={`/services/${item.slug}`} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" /> View Page
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(item.service_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Services;
